import { useState, useEffect, useRef } from 'react';
import { X } from 'react-bootstrap-icons';
import './VisitCounter.css';

const OS_ICONS = {
  'Windows 10': '⊞', 'Windows 11': '⊞', 'Windows': '⊞',
  'Mac': '',
  'Android': '⬡',
  'iOS': '📱',
  'Ubuntu': '◉',
  'GNU/Linux': '🐧',
};

function plural(n, s) {
  return n === 1 ? s : s + 's';
}

function countryFlag(code) {
  if (!code || code === 'unknown' || code.length !== 2) return '🏳';
  const a = code.charCodeAt(0) - 65 + 0x1F1E6;
  const b = code.charCodeAt(1) - 65 + 0x1F1E6;
  return String.fromCodePoint(a, b);
}

function fmtTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function VisitCounter() {
  const [count, setCount] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const recorded = useRef(false);

  useEffect(() => {
    if (recorded.current) return;
    recorded.current = true;

    const register = async () => {
      try {
        const res = await fetch('/api/visit-counter', { method: 'POST' });
        const data = await res.json();
        if (data.count !== undefined) setCount(data.count);
      } catch {
        setCount(0);
      }
    };
    register();
  }, []);

  const handleClick = async () => {
    if (open) { setOpen(false); return; }
    setLoading(true);
    setOpen(true);
    try {
      const res = await fetch('/api/visit-counter');
      const data = await res.json();
      setAnalytics(data);
    } catch {
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setOpen(false);

  if (count === null) return null;

  return (
    <>
      <span className="visit-counter" onClick={handleClick}>
        visits {count}
      </span>

      {open && (
        <div className="visit-sidebar-overlay" onClick={handleClose}>
          <div className="visit-sidebar" onClick={e => e.stopPropagation()}>
            <div className="visit-sidebar-header">
              <div>
                <h2 className="visit-sidebar-title">Live Analytics</h2>
                <div className="visit-sidebar-subtitle">facucorona.dev.ar · real-time</div>
              </div>
              <button className="visit-sidebar-close" onClick={handleClose}><X /></button>
            </div>

            {loading && <div className="visit-sidebar-loading">Loading...</div>}

            {!loading && analytics && (
              <div className="visit-sidebar-content">
                <div className="visit-sidebar-stats-row">
                  <div className="visit-sidebar-stat-card highlight">
                    <span className="visit-sidebar-stat-number">{analytics.thisMonthViews}</span>
                    <span className="visit-sidebar-stat-label">This Month</span>
                    <span className="visit-sidebar-stat-sub">{plural(analytics.thisMonthViews, 'page view')}</span>
                  </div>
                  <div className="visit-sidebar-stat-card">
                    <span className="visit-sidebar-stat-number">{analytics.uniqueIps}</span>
                    <span className="visit-sidebar-stat-label">Total</span>
                    <span className="visit-sidebar-stat-sub">{plural(analytics.uniqueIps, 'unique visitor')}</span>
                  </div>
                </div>

                <div className="visit-sidebar-stat-card full">
                  <span className="visit-sidebar-stat-number">{analytics.count}</span>
                  <span className="visit-sidebar-stat-label">All Time</span>
                  <span className="visit-sidebar-stat-sub">{plural(analytics.count, 'total view')}</span>
                </div>

                <div className="visit-sidebar-section">
                  <h3 className="visit-sidebar-section-title">Audience · Countries</h3>
                  <div className="visit-sidebar-list">
                    {(() => {
                      const entries = Object.entries(analytics.countries).sort((a, b) => b[1] - a[1]);
                      const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                      const top5 = entries.slice(0, 5);
                      const rest = entries.slice(5);
                      const restSum = rest.reduce((s, [, v]) => s + v, 0);
                      return (
                        <>
                          {top5.map(([country, val], i) => (
                            <div key={country} className="visit-sidebar-row">
                              <span className="visit-sidebar-row-rank">{i + 1}</span>
                              <span className="visit-sidebar-row-bar-wrap">
                                <span className="visit-sidebar-row-bar" style={{ width: `${(val / total) * 100}%` }} />
                              </span>
                              <span className="visit-sidebar-row-label">{country}</span>
                              <span className="visit-sidebar-row-value">{Math.round((val / total) * 100)}%</span>
                            </div>
                          ))}
                          {rest.length > 0 && (
                            <div className="visit-sidebar-row-more">
                              + {rest.length} more {plural(rest.length, 'country')}
                              <span className="visit-sidebar-row-value">{Math.round((restSum / total) * 100)}%</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="visit-sidebar-section">
                  <h3 className="visit-sidebar-section-title">Devices · OS</h3>
                  <div className="visit-sidebar-list">
                    {(() => {
                      const entries = Object.entries(analytics.os).sort((a, b) => b[1] - a[1]);
                      const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                      return entries.map(([os, val]) => (
                        <div key={os} className="visit-sidebar-row">
                          <span className="visit-sidebar-row-icon">{OS_ICONS[os] || '⬡'}</span>
                          <span className="visit-sidebar-row-bar-wrap">
                            <span className="visit-sidebar-row-bar" style={{ width: `${(val / total) * 100}%` }} />
                          </span>
                          <span className="visit-sidebar-row-label">{os}</span>
                          <span className="visit-sidebar-row-value">{Math.round((val / total) * 100)}%</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="visit-sidebar-section">
                  <h3 className="visit-sidebar-section-title">Recent · Last 20 Visitors</h3>
                  <div className="visit-sidebar-history">
                    {analytics.recentVisits && analytics.recentVisits.length > 0 ? (
                      analytics.recentVisits.map((v, i) => (
                        <div key={i} className="visit-sidebar-history-row">
                          <span className="visit-sidebar-history-flag">{countryFlag(v.countryCode)}</span>
                          <span className="visit-sidebar-history-country">{v.country}</span>
                          <span className="visit-sidebar-history-ip">{v.ip}</span>
                          <span className="visit-sidebar-history-os">{v.os}</span>
                          <span className="visit-sidebar-history-time">{fmtTime(v.timestamp)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="visit-sidebar-history-empty">No recent visits</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!loading && !analytics && (
              <div className="visit-sidebar-loading">Error loading analytics</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VisitCounter;
